import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { useSettings } from '../../context/SettingsContext.jsx';
import { useTheme } from '../../context/ThemeContext.jsx';
import { uploadUrl } from '../../api/client.js';
import { MORPH_BTN_SM } from '../../lib/ui.js';
import AdminBottomNav from './AdminBottomNav.jsx';

const MENU = [
  { to: '/admin/dashboard', icon: 'speed', label: 'Dashboard' },
  { to: '/admin/carousel', icon: 'photo_library', label: 'Hero Carousel' },
  { to: '/admin/layanan', icon: 'grid_view', label: 'Layanan' },
  { to: '/admin/portfolio', icon: 'photo_camera', label: 'Portfolio' },
  { to: '/admin/testimonial', icon: 'format_quote', label: 'Testimonial' },
  { to: '/admin/kontak', icon: 'mail', label: 'Pesan Masuk' },
  { to: '/admin/booking', icon: 'event_available', label: 'Jadwal Booking' },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { dark, toggleDark } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const color = settings.app_color || 'var(--accent)';
  const useLogo = settings.app_logo_mode === 'logo' && !!settings.app_logo;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 transition-colors duration-300">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-[86vw] max-w-[360px] md:w-64 md:max-w-none z-[9999] flex flex-col rounded-tr-3xl rounded-br-3xl md:rounded-none overflow-hidden md:border-r md:border-black/5 md:dark:border-white/5 bg-white dark:bg-neutral-900 transition-transform duration-300 will-change-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="flex items-center gap-3 px-6 py-6 md:py-5 border-b border-black/5 dark:border-white/5">
          {useLogo ? (
            <img src={uploadUrl(settings.app_logo)} alt={settings.site_name} className="h-11 md:h-9 w-auto max-w-[200px] md:max-w-[180px] object-contain object-left" />
          ) : (
            <>
              <div className="w-11 h-11 md:w-9 md:h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
                <span className="material-symbols-outlined text-white text-xl md:text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>{settings.app_icon || 'pets'}</span>
              </div>
              <div>
                <div className="text-sm md:text-xs font-bold font-display truncate" style={{ color }}>{(settings.site_name || '').toUpperCase()}</div>
                <div className="text-sm md:text-xs text-neutral-500 dark:text-neutral-400 font-medium">Admin Panel</div>
              </div>
            </>
          )}
        </div>

        <nav className="flex-1 px-4 md:px-3 py-5 md:py-4 overflow-y-auto">
          <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider px-4 mb-3 md:mb-2 mt-1">Menu</p>
          <div className="space-y-1 md:space-y-0.5">
            {MENU.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `dn-link flex items-center gap-3.5 md:gap-3 px-4 py-3.5 md:py-2.5 rounded-xl text-base md:text-sm font-semibold ${MORPH_BTN_SM} ${
                    isActive
                      ? 'dn-active bg-[var(--accent-pill-bg)] text-[var(--accent-active-text)] dark:bg-[var(--accent-pill-bg-dark)] dark:text-[var(--accent-active-text-dark)]'
                      : 'text-[#262626] dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className="material-symbols-outlined text-xl md:text-base"
                      style={{ fontVariationSettings: `'FILL' ${isActive ? 1 : 0}` }}
                    >
                      {item.icon}
                    </span>
                    {item.label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
          <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider px-4 mb-3 md:mb-2 mt-8">Pengaturan</p>
          <NavLink to="/admin/settings" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `dn-link flex items-center gap-3.5 md:gap-3 px-4 py-3.5 md:py-2.5 rounded-xl text-base md:text-sm font-semibold ${MORPH_BTN_SM} ${isActive ? 'dn-active bg-[var(--accent-pill-bg)] text-[var(--accent-active-text)] dark:bg-[var(--accent-pill-bg-dark)] dark:text-[var(--accent-active-text-dark)]' : 'text-[#262626] dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}>
            {({ isActive }) => (
              <>
                <span className="material-symbols-outlined text-xl md:text-base" style={{ fontVariationSettings: `'FILL' ${isActive ? 1 : 0}` }}>settings</span>
                Pengaturan Situs
              </>
            )}
          </NavLink>
        </nav>

        <div className="px-4 md:px-3 py-5 md:py-4 border-t border-black/5 dark:border-white/5">
          <Link to="/" target="_blank" className={`dn-link flex items-center gap-3.5 md:gap-3 px-4 py-3.5 md:py-2.5 rounded-xl text-base md:text-sm font-medium text-[#262626] dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 ${MORPH_BTN_SM}`}>
            <span className="material-symbols-outlined text-lg md:text-sm">open_in_new</span> Lihat Website
          </Link>
          <button onClick={doLogout} className={`dn-link w-full flex items-center gap-3.5 md:gap-3 px-4 py-3.5 md:py-2.5 rounded-xl text-base md:text-sm font-medium text-red-500 hover:bg-red-500 hover:text-white ${MORPH_BTN_SM}`}>
            <span className="material-symbols-outlined text-lg md:text-sm">logout</span> Keluar
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-[9998] bg-black/40 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main content */}
      <div className="md:ml-64 flex flex-col min-h-screen relative">
        <div
          className="fixed top-0 left-0 right-0 md:left-64 z-30 h-[104px] pointer-events-none transition-opacity duration-300"
          style={{
            opacity: scrolled ? 1 : 0,
            backdropFilter: scrolled ? 'blur(14px)' : 'blur(0px)',
            background: dark
              ? 'linear-gradient(to bottom, rgba(10,15,10,0.8), transparent)'
              : 'linear-gradient(to bottom, rgba(249,250,251,0.8), transparent)',
            maskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 100%)',
          }}
        />
        <header className="sticky top-0 z-40 px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            {/* Kiri: hamburger pill (mobile) + judul halaman pill */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen((v) => !v)}
                className="md:hidden flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)] text-[#475569] dark:text-[#94a3b8] transition-all duration-300 hover:rounded-2xl active:rounded-[16px] active:scale-95"
              >
                <span className="material-symbols-outlined text-2xl">menu</span>
              </button>
              <div className="min-w-0 flex items-center h-12 rounded-full pl-5 pr-5 border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)]">
                <h1 className="text-sm sm:text-base font-bold text-neutral-800 dark:text-white truncate">{title}</h1>
              </div>
            </div>

            {/* Kanan: dark mode + akun, dikelompokkan satu pill seperti navbar public */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex items-center h-12 px-1 rounded-full border border-white/50 dark:border-neutral-700/60 bg-white/90 dark:bg-neutral-800/90 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.55)]">
                <button onClick={toggleDark} className="dn-link flex items-center justify-center w-10 h-10 rounded-full text-[#475569] dark:text-[#94a3b8] hover:bg-[rgba(74,122,58,0.08)] hover:text-[#4A7A3A] hover:rounded-2xl active:rounded-[14px] dark:hover:bg-[rgba(126,201,90,0.12)] dark:hover:text-[#7EC95A]">
                  <span className="material-symbols-outlined text-lg">{dark ? 'light_mode' : 'dark_mode'}</span>
                </button>

                <div className="w-px h-9 mx-0.5 bg-neutral-200 dark:bg-neutral-700" />

                <div className="relative" ref={menuRef}>
                  <button onClick={() => setMenuOpen((v) => !v)} className="dn-link flex items-center gap-2 h-10 pl-1 pr-3 rounded-full hover:bg-[rgba(74,122,58,0.08)] hover:rounded-2xl active:rounded-[14px] active:scale-[0.98] dark:hover:bg-[rgba(126,201,90,0.12)] transition-all duration-300">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: `linear-gradient(135deg, ${color}, #FFC72C)` }}>
                      {(user?.name || user?.username || '?')[0]?.toUpperCase()}
                    </div>
                    <div className="hidden sm:flex flex-col leading-tight text-left">
                      <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{user?.name || user?.username}</span>
                      <span className="text-[11px] text-neutral-400">Administrator</span>
                    </div>
                    <span className="material-symbols-outlined text-base text-neutral-400 hidden sm:inline-block transition-transform duration-200" style={{ transform: menuOpen ? 'rotate(180deg)' : 'none' }}>expand_more</span>
                  </button>

                  {menuOpen && (
                    <div className="absolute right-0 top-[calc(100%+0.75rem)] w-52 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xl z-30 overflow-hidden">
                      <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 sm:hidden">
                        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{user?.name || user?.username}</p>
                        <p className="text-[11px] text-neutral-400">Administrator</p>
                      </div>
                      <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300">
                        <span className="material-symbols-outlined text-base">logout</span> Keluar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-5 md:p-8 overflow-x-hidden">{children}</main>

        <footer className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
          <p className="text-xs text-neutral-400 dark:text-neutral-600 text-center">
            &copy; {new Date().getFullYear()} {settings.site_name} &mdash; Admin Panel
          </p>
        </footer>
      </div>

      <AdminBottomNav />
    </div>
  );
}
